import { Component, ElementRef, OnDestroy, OnInit, signal, ViewChild, WritableSignal } from '@angular/core';
import { TraceService } from '../../../services/trace.service';
import { TraceRouteData} from '../../../models/trace-route-data';
import { BrowserSnifferService } from '../../../services/browser-sniffer.service';
import { AlertService } from '../../../services/alert.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FileUploaderComponent } from '../../file-uploader/file-uploader.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { take } from 'rxjs/operators';
import { TraceCreateConfirmationDialogComponent, TraceCreateConfirmationDialogData } from '../trace-create/trace-create-confirmation-dialog/trace-create-confirmation-dialog.component';
import { Observable, Subscription } from 'rxjs';
import { TraceRouteHistory, TraceRouteHistoryItemType } from '../../../models/trace-route-history';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TraceUploaderComponent } from './trace-uploader/trace-uploader.component';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { ImportService } from './import.service';
import { ToggleEventNameType, ToggleEventService } from '@tt-lib/services/toggle-event.service';
import { Capacitor } from '@capacitor/core';
import { BackEventService } from '@tt-lib/services/back-event.service';

@Component({
  selector: 'tt-trace-import',
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatExpansionModule,
    MatTooltipModule,
    MatDividerModule,
    MatCardModule,
    TranslateModule,
    FormsModule,
    TraceUploaderComponent,
    // FileUploaderComponent
  ],
  templateUrl: './trace-import.component.html',
  styleUrls: ['../trace.scss'],
  standalone: true
})

export class TraceImportComponent implements OnInit, OnDestroy {
  // private _mapElement: HTMLElement;
  // private _olControlElements: NodeListOf<HTMLElement>;
  private _confirmationDialog: MatDialogRef<TraceCreateConfirmationDialogComponent, { data: TraceCreateConfirmationDialogData }>;
  private _subscriptions: Subscription;
  public ref_historyItems: WritableSignal<Array<TraceRouteHistoryItemType>> = signal([]);
  public lang: WritableSignal<string> = signal('en');
  @ViewChild('fileUploader')
  private _fileUploader: FileUploaderComponent;

  public readonly allowedMimeTypes!: Array<string>;
  public readonly allowedExtensions!: Array<string>;

  constructor(
    public importService: ImportService,
    private _alertService: AlertService,
    private _translateService: TranslateService,
    public browsersniffer: BrowserSnifferService,
    public _traceService: TraceService,
    private _dialog: MatDialog,
    private _toggleEventService: ToggleEventService,
    private _backEventService: BackEventService
  ) {
    this.allowedMimeTypes = ['.gpx', 'application/gpx+xml', 'application/gpx', 'application/vnd.google-earth.gpx', 'application/vnd.google-earth.gpx+xml', 'text/gpx', 'text/gpx+xml', 'text/xml+gpx'];
    if(Capacitor.getPlatform() === 'android') {
      //otherwise gpx files will not be selectable when browsing via a input file tag
      //this.allowedMimeTypes.push('*/*');
      this.allowedMimeTypes = ['*/*'];
    }
    this.allowedExtensions = ['gpx'];

    this._confirmationDialog = null;

    this.ref_historyItems.set(this._getFilteredHistoryItems(this._traceService.getCopyOfTraceRouteHistoryItems().slice().reverse()));
    this._subscriptions = new Subscription();
    this._subscriptions.add(
      this._traceService.updatedTraceRouteHistory$.subscribe(() => {
        this.ref_historyItems.set(this._getFilteredHistoryItems(this._traceService.getCopyOfTraceRouteHistoryItems().slice().reverse()));
      })
    );

    this.lang.set(this._translateService.getCurrentLang());
    this._subscriptions.add(
      this._translateService.onLangChange
        .subscribe((langData) => {
          this.lang.set(langData.lang)
        }));
  }

  ngOnInit(): void {
  
    /*
    //moved to import service
    //launcher for associated gpx files
    let win: any = window;
    if ("launchQueue" in win) {
      win.launchQueue.setConsumer((launchParams) => {
        // Nothing to do when the queue is empty.
        if (!launchParams.files.length) {
          return;
        }
        // Handle the first file only.
        if (launchParams.files.length) {
          // this.handleAssociatedGpxFile(launchParams.files[0]);
          return
        }
      });
    }
    */
  }
  ngAfterViewInit() {
    this._subscriptions.add(
      this.importService.isLoadingFile$?.subscribe((bool) => {
        this._fileUploader.isLoadingFile = bool;
      })
    );
    this._subscriptions.add(
      this.importService.showImportError$?.subscribe((error) => {
        if(error)this.importService.showImportError(error);
      })
    )
  }
  // async handleAssociatedGpxFile(file) {
  //   const blob = await file.getFile();
  //   this.importService.importGpxFile(blob);
 // }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }



  private _getFilteredHistoryItems(historyItems: TraceRouteHistoryItemType[]): TraceRouteHistoryItemType[] {
    const filteredHistoryItems = [];
    if (historyItems) {
      const emptyThumb = this._traceService.getEmptyThumbnail();
      historyItems.forEach((historyItem: TraceRouteHistoryItemType) => {
        //contains title, description, distance > 0 or has thumbnail?
        if ((historyItem.name && historyItem.name.length > 0) || historyItem.hasDesc || historyItem.dist || (historyItem.thumb && historyItem.thumb.length > 0 && historyItem.thumb !== emptyThumb)) {
          filteredHistoryItems.push(historyItem);
        }
      });
    }
    return filteredHistoryItems;
  }

  confirmToReplaceExistingData(): Observable<any> {
    if (this._confirmationDialog !== null) {
      this._confirmationDialog.close();
      this._confirmationDialog = null;
    }
    this._confirmationDialog = this._dialog.open(TraceCreateConfirmationDialogComponent, {
      panelClass: 'small-dialog',
      data: {
        title: this._translateService.instant('trace.replaceData'),
        text: this._translateService.instant('trace.confirm-replaceData'),
        type: 'replaceData'
      },
    });
    this._backEventService.registerOpenedWindow({windowName: 'TraceCreateConfirmationDialogComponent', isAutoCloseableWindow: true});
    return this._confirmationDialog.afterClosed();
  }

  onDrop(ev: DragEvent) {
    let validFileFound: boolean = false;
    let file: File;
    if (ev.dataTransfer.items) {
      // Use DataTransferItemList interface to access the file(s)
      for (let i = 0; i < ev.dataTransfer.items.length; i++) {
        // If dropped items aren't files, reject them
        if (ev.dataTransfer.items[i].kind === 'file') {
          file = ev.dataTransfer.items[i].getAsFile();
          if (file.name.toLowerCase().endsWith('.gpx')) {
            validFileFound = true;
            break;
          }
        }
      }
    } else {
      // Use DataTransfer interface to access the file(s)
      for (let i = 0; i < ev.dataTransfer.files.length; i++) {
        if (ev.dataTransfer.files[i].name.toLowerCase().endsWith('.gpx')) {
          validFileFound = true;
          file = ev.dataTransfer.files[i];
           this.importService.importGpxFile(ev.dataTransfer.files[i]);
          break;
        }
      }
    }
    //confirmationdialog
    if (this._traceService.hasTraceRouteOrWaypointOnMap()) {
      this.confirmToReplaceExistingData().pipe(take(1)).subscribe(result => {
        if (result && result.data && result.data.type === 'replaceData') {
          if (validFileFound) {
             this.importService.importGpxFile(file);
          } else {
            this.showImportError(null);
          }
        }
        else {
          this._fileUploader.back();
        }
        this._confirmationDialog = null;
      });
    }
    else {
      if (validFileFound) {
         this.importService.importGpxFile(file);
      } else {
        this.showImportError(null);
      }
    }
  }

  onImportGpxFile(gpxFileElement: ElementRef<HTMLInputElement>) {
    let validFileFound: boolean = false;
    let file: File;
    if (
      gpxFileElement.nativeElement.files.length > 0 &&
      gpxFileElement.nativeElement.files[0].name
        .toLowerCase()
        .endsWith('.gpx')
    ) {
      file = gpxFileElement.nativeElement.files[0];
      validFileFound = true;
    }
    //confirmationdialog
    if (this._traceService.hasTraceRouteOrWaypointOnMap()) {
      this.confirmToReplaceExistingData().pipe(take(1)).subscribe(result => {
        if (result && result.data && result.data.type === 'replaceData') {
          if (validFileFound) {
             this.importService.importGpxFile(file);
          } else {
            this.showImportError(null);
          }
        }
        else {
          this._fileUploader.back();
        }
        this._confirmationDialog = null;
      });
    }
    else {
      if (validFileFound) {
         this.importService.importGpxFile(file);
      } else {
        this.showImportError(null);
      }
    }
    gpxFileElement.nativeElement.value = '';
  }

  showImportError(error: any) {
    this._fileUploader.showImportError();

    if (error) {
      console.log("an error occured during import:");
      console.log(error);

      if (typeof error.message === "string" && error.message === "point outside map extent") {
        this._alertService.info(this._translateService.instant('trace.import-outside-map'), { 'autoClose': true });
      }
    }
  }

  openHistoryItem(dataRef: string, appId: number | string) {

    //there will be a zoom after data load
    //geolocalisation component is listening
    this._toggleEventService.toggleEvent({
          forwardedFor: 'trace-histo-import',
          eventName: ToggleEventNameType.FILE_IMPORT
    });

    // mobile when editing is active
    if (this._traceService.isInEditingMode()) {
      this._traceService.closeCreateMobile();
    }
    this._traceService.resetTraceRouteData(true);

    if (TraceRouteHistory.isLocalStorageDataReference(dataRef)) {
      this._traceService.loadTraceRouteDataFromLocalStorage(false, true, dataRef);
    } else {
      this._traceService.loadTraceRouteDataFromExternalStorage(true, appId, dataRef).subscribe();
    }

    window.setTimeout(() => {
      this._traceService.fullCleanupAndSaveForHistoryItems(dataRef);
    }, 1000);
  }

  deleteHistoryItem(dataRef: string) {
    if (this._confirmationDialog !== null) {
      this._confirmationDialog.close();
      this._confirmationDialog = null;
    }
    const isEditedHistoryItem = this._traceService.isEditedHistoryItem(dataRef);
    this._confirmationDialog = this._dialog.open(TraceCreateConfirmationDialogComponent, {
      panelClass: 'small-dialog',
      data: {
        title: this._translateService.instant('trace.import.remove-historylist-title'),
        text: (isEditedHistoryItem ? this._translateService.instant('trace.import-deletehistory-localdata-alert') + '<br>' : '') + this._translateService.instant('trace.import-deletehistory-confirm'),
        type: dataRef
      },
    });
    
    this._confirmationDialog.afterClosed().pipe(take(1)).subscribe(result => {
      if (result && result.data && result.data.type) {
        const currenPermalinkToken = this._traceService.getPermalinkParam();
        if (currenPermalinkToken && currenPermalinkToken === result.data.type) {
          this._traceService.resetTraceRouteData(true);
        }
        this._traceService.removeHistoryItem(result.data.type);
      }
      this._confirmationDialog = null;
    });

    this._backEventService.registerOpenedWindow({windowName: 'TraceCreateConfirmationDialogComponent', isAutoCloseableWindow: true});
  }

  formatRouteDistance(distance: number, isUpwardDistance: boolean): string {
    return typeof distance === "number" && distance > -1 ? TraceRouteData.formatRouteLength(distance, true, '0 <span>m</span>') : isUpwardDistance ? '_' : '0 <span>m</span>';
  }

  formatRouteTime(time: number): string {
    return TraceRouteData.formatTime(time, true, true, '');
  }
}
